import React from 'react'
import Chart from 'react-apexcharts'
import { format } from 'date-fns'
import { Legend } from './Legend/Legend'
import { COLOR_SCALE, COLORS, getDateLabel, getWeekDayLabel } from '../openingTimeHelpers'
import { useTranslation } from 'react-i18next'
import { LegendOverall } from './Legend/LegendOverall'

const hoursInDay = Array.from({length: 24}, (_, i) => String(i).padStart(2, '0'))
const BASE_HEIGHT = 100
const ROW_HEIGHT = 35

const WEEKDAY_ORDER = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
}

const isDateFormat = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date)

const getCellValue = (regularTime, offTime, pausedTime, isWeekly) => {
    if (regularTime === 0) return 0

    const actualOpenTime = Math.max(0, regularTime - offTime - pausedTime)

    // If supposed to be open but wasn't (100% pause/off time), show as 1% (red) instead of 0% (gray)
    if (actualOpenTime === 0) return 1

    const base = isWeekly ? 60 : regularTime
    return Math.round((actualOpenTime / base) * 100)
}

const getColorForPercentage = (percentage, colorScale) => {
    const range = colorScale.find(r => percentage >= r.from && percentage <= r.to)
    return range ? range.color : COLORS.CLOSED
}

const HeatMapOpeningTimes = React.memo(({openingTimes, isWeekly = false, colorScale = COLOR_SCALE }) => {
    const [t] = useTranslation()

    const sortedDays = [...openingTimes].sort((a, b) => {
        if (isDateFormat(a.date)) {
            return new Date(b.date) - new Date(a.date)
        }

        return WEEKDAY_ORDER[b.date] - WEEKDAY_ORDER[a.date]
    })

    const series = sortedDays.map((day) => {
        const map = new Map()

        day.hours?.forEach((h) => {
            const regularTime = h.regularOpeningTimeInMinutes ?? 0
            const offTime = h.offTimeInMinutes ?? 0
            const pausedTime = h.pausedTimeInMinutes ?? 0
            const cellValue = getCellValue(regularTime, offTime, pausedTime, isWeekly)

            map.set(h.hour.padStart(2, '0'), {
                cellValue,
                regularTime,
                pausedTime,
                offTime,
            })
        })

        return {
            name: isWeekly ? t(`days.${getWeekDayLabel(day)}`) : getDateLabel(day),
            data: hoursInDay.map((hr) => {
                const data = map.get(hr) ?? {cellValue: 0, regularTime: 0, pausedTime: 0, offTime: 0}
                return {
                    x: hr,
                    y: data.cellValue,
                    regularTime: data.regularTime,
                    pausedTime: data.pausedTime,
                    offTime: data.offTime,
                }
            }),
            averageOpenRate: day.averageOpenRate,
        }
    })

    const height = BASE_HEIGHT + (series.length * ROW_HEIGHT)
    const today = format(new Date(), 'yyyy-MM-dd')
    const daysForBars = sortedDays.filter(day => day.date !== today)

    return (
      <div>
          <div style={{display: 'flex', alignItems: 'flex-start', height: `${height}px`}}>
              <div style={{flex: '1'}}>
                  <Chart
                    options={{
                        chart: {
                            type: 'heatmap',
                            toolbar: {show: false},
                            animations: {
                                enabled: false,
                                dynamicAnimation: {enabled: false}
                            },
                        },
                        dataLabels: {enabled: false},
                        grid: {padding: {left: 8, right: 8, top: 8, bottom: 8}},
                        plotOptions: {
                            heatmap: {
                                enableShades: false,
                                useFillColorAsStroke: false,
                                colorScale: {ranges: colorScale},
                            },
                        },
                        legend: {show: false},
                        xaxis: {
                            type: 'category',
                            categories: hoursInDay,
                            tickAmount: 24,
                            labels: {
                                rotate: -45,
                                style: {fontSize: '11px'},
                                formatter: (val) => `${val}h`,
                            },
                            axisTicks: {show: false},
                        },
                        yaxis: {
                            labels: {style: {fontSize: '12px'}},
                        },
                        tooltip: {
                            custom: function ({seriesIndex, dataPointIndex, w}) {
                                const dataPoint = w.config.series[seriesIndex].data[dataPointIndex]
                                const hour = hoursInDay[dataPointIndex]

                                const regularOpeningTime = dataPoint.regularTime || 0
                                const pausedTimeInMinutes = dataPoint.pausedTime || 0
                                const offTimeInMinutes = dataPoint.offTime || 0
                                const actualOpenTime = Math.max(0, regularOpeningTime - pausedTimeInMinutes - offTimeInMinutes)

                                let pct
                                if (isWeekly) {
                                    pct = regularOpeningTime > 0 ? Math.round((actualOpenTime / 60) * 100) : 0
                                } else {
                                    pct = regularOpeningTime > 0 ? Math.round((actualOpenTime / regularOpeningTime) * 100) : 0
                                }

                                return `
                                    <div style="padding: 8px 12px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
                                        <div style="font-weight: 600; margin-bottom: 6px; font-size: 13px; color: #000;">
                                            ${hour}h00–${hour}h59
                                        </div>
                                        <div style="font-size: 12px; line-height: 1.6; color: #000;">
                                            ${regularOpeningTime === 0 ? `<div>
                                                <strong>${t('openings.opening_times.tooltip.closed')}</strong>
                                            </div>` : `<div>
                                                <strong>${t('openings.opening_times.tooltip.open', {
                                    minutes: actualOpenTime,
                                    rate: pct,
                                })}</strong>
                                            </div>`}
                                            ${pausedTimeInMinutes > 0 ? `<div>
                                                <strong>${t('openings.opening_times.tooltip.pause', {minutes: pausedTimeInMinutes})}</strong>
                                            </div>` : ''}
                                            ${offTimeInMinutes > 0 ? `<div>
                                                <strong>${t('openings.opening_times.tooltip.off_day', {minutes: offTimeInMinutes})}</strong>
                                            </div>` : ''}
                                        </div>
                                    </div>
                                `
                            },
                        },
                        stroke: {show: true, width: 2, colors: ['#ffffff']},
                    }}
                    series={series}
                    type="heatmap"
                    height={height}
                  />
              </div>

              <div style={{
                  width: '70px',
                  height: `${height - (isWeekly ? 66 : sortedDays.some(day => day.date === today) ? 102 : 66)}px`,
                  marginTop: '30px',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  justifyContent: 'space-evenly',
              }}>
                  {daysForBars.map((day) => {
                      const percentage = day.averageOpenRate
                      const color = getColorForPercentage(percentage, colorScale)

                      return (
                        <div key={day.date}>
                            <div style={{
                                width: '62px',
                                height: '20px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '3px',
                                overflow: 'hidden',
                                position: 'relative',
                                border: '1px solid #e5e7eb',
                            }}>
                                <div style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    backgroundColor: color,
                                }}/>
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: `${percentage > 68 ? '#ffffff' : '#000000'}`,
                                }}>
                                    {percentage}%
                                </div>
                            </div>
                        </div>
                      )
                  })}
              </div>
          </div>

          {isWeekly ? <LegendOverall /> : <Legend />}
      </div>
    )
})

export { HeatMapOpeningTimes }
